import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { ProgressState, ResourceAmounts } from '../../lib/game';
import { formatDurationShort } from '../../lib/format';
import { Badge } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { CostList } from './CostList';
import { ClockIcon, LockIcon } from '../../icons/ui-icons';

export interface EntityCardProps {
  name: ReactNode;
  description?: ReactNode;
  /** Rendered artwork — the isometric asset from the GDD prompt pipeline. */
  art?: ReactNode;
  /** Fallback glyph shown while art is missing. */
  icon?: ReactNode;
  /** Current level (buildings, research, defence tiers). */
  level?: number;
  maxLevel?: number;
  /** Owned count — for ships and defence structures. */
  owned?: number;
  state?: ProgressState;
  cost?: ResourceAmounts;
  available?: ResourceAmounts;
  /** Build/research duration in seconds. */
  duration?: number;
  /** Requirement text shown when `state` is `locked`. */
  requirement?: ReactNode;
  /** Extra readouts — combat stats, production deltas. */
  stats?: ReactNode;
  /** Label for the primary action. Defaults to "Ausbauen". */
  actionLabel?: string;
  onAction?: () => void;
  /** Secondary controls — details, demolish, queue multiple. */
  secondaryAction?: ReactNode;
  className?: string;
}

const STATE_BADGE: Record<
  ProgressState,
  { label: string; accent: 'neutral' | 'silicate' | 'danger' | 'warning' | 'success' } | null
> = {
  locked: { label: 'Gesperrt', accent: 'neutral' },
  available: null,
  insufficient: { label: 'Rohstoffe fehlen', accent: 'warning' },
  active: { label: 'Im Bau', accent: 'silicate' },
  damaged: { label: 'Beschädigt', accent: 'danger' },
  maxed: { label: 'Max. Stufe', accent: 'success' },
};

/**
 * Shared card shell for every buildable entity.
 *
 * Buildings, ships, defences and research all present the same decision to the
 * player — *what is it, what does it cost, how long does it take, can I start
 * it* — so they share one layout and one state machine. The specialised cards
 * below only supply the parts that genuinely differ.
 *
 * `state` drives everything: it picks the badge, disables the action and, for
 * `damaged`, switches the card to the repair treatment the comet event needs.
 */
export function EntityCard({
  name,
  description,
  art,
  icon,
  level,
  maxLevel,
  owned,
  state = 'available',
  cost,
  available,
  duration,
  requirement,
  stats,
  actionLabel = 'Ausbauen',
  onAction,
  secondaryAction,
  className,
}: EntityCardProps) {
  const badge = STATE_BADGE[state];
  const locked = state === 'locked';
  const actionable = state === 'available' || state === 'damaged';

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-panel border bg-plate shadow-panel',
        'transition-colors duration-[var(--sg-duration)]',
        state === 'damaged'
          ? 'border-danger/45'
          : state === 'active'
            ? 'border-silicate/40'
            : 'border-edge hover:border-edge-strong',
        locked && 'opacity-65',
        className,
      )}
    >
      {/*
        Artwork plate. Until the generated isometric assets land, the fallback
        has to look deliberate rather than like a failed image load — so the
        placeholder is a lit emblem on the engineering grid, not a small grey
        glyph floating in a large empty box. 16/9 keeps a card row on a rhythm
        without the plate dominating the card.
      */}
      <div className="relative aspect-[16/9] overflow-hidden border-b border-edge-subtle bg-surface-base">
        <div className="absolute inset-0 bg-grid bg-grid-sm opacity-40" aria-hidden />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 70% at 50% 55%, rgb(var(--sg-silicate) / 0.1) 0%, transparent 70%)',
          }}
        />
        {art ?? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={cn(
                'flex h-16 w-16 items-center justify-center rounded-panel border',
                'border-edge bg-surface-2/80 text-content-muted shadow-panel',
              )}
            >
              {icon}
            </div>
          </div>
        )}

        {locked ? (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-overlay/70">
            <LockIcon size={22} className="text-content-muted" />
          </div>
        ) : null}

        {level !== undefined ? (
          <span
            data-sg-numeric
            className={cn(
              'absolute left-2 top-2 rounded-control border border-edge bg-surface-overlay/85',
              'px-1.5 py-0.5 font-display text-label-sm font-semibold text-content',
            )}
          >
            Stufe {level}
            {maxLevel !== undefined ? `/${maxLevel}` : ''}
          </span>
        ) : null}

        {owned !== undefined ? (
          <span
            data-sg-numeric
            className={cn(
              'absolute left-2 top-2 rounded-control border border-edge bg-surface-overlay/85',
              'px-1.5 py-0.5 font-display text-label-sm font-semibold text-content',
            )}
          >
            ×{owned}
          </span>
        ) : null}

        {badge ? (
          <Badge
            accent={badge.accent}
            size="sm"
            pulse={state === 'active'}
            className="absolute right-2 top-2"
          >
            {badge.label}
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-3">
        <div>
          <h4 className="font-display text-xs font-semibold uppercase tracking-[0.1em] text-content">
            {name}
          </h4>
          {description ? (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-content-muted">
              {description}
            </p>
          ) : null}
        </div>

        {stats}

        {locked && requirement ? (
          <p className="text-xs text-content-muted">
            <LockIcon size={12} className="mr-1 inline align-[-2px]" />
            {requirement}
          </p>
        ) : null}

        {cost && !locked ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CostList cost={cost} available={available} />
            {duration !== undefined ? (
              <span className="inline-flex items-center gap-1 text-xs text-content-muted">
                <ClockIcon size={12} />
                <span data-sg-numeric>{formatDurationShort(duration)}</span>
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Actions pin to the bottom so cards in a row align regardless of body length. */}
        <div className="mt-auto flex items-center gap-2 pt-1">
          {onAction ? (
            <Button
              variant={state === 'damaged' ? 'danger' : 'primary'}
              size="sm"
              fullWidth
              disabled={!actionable}
              onClick={onAction}
            >
              {state === 'damaged' ? 'Reparieren' : actionLabel}
            </Button>
          ) : null}
          {secondaryAction}
        </div>
      </div>
    </article>
  );
}
